import { Component, OnInit } from '@angular/core';
import { debounceTime, merge, startWith, Subject, takeUntil } from 'rxjs';
import { InvestmentControllingService } from 'src/app/services/investment-controlling.service';
import { TableUtils } from 'src/app/utils/table-utils';
import { TableColumn } from '../../../shared-components/data-table/data-table.component';
import { IncoOplPeriodsInfo } from 'src/app/interfaces/investmentcontrolling/inco-opl-periods-info';
import { DialogService } from 'src/app/services/dialog.service';
import { DefaultValuesService } from 'src/app/services/defaultvalues.service';
import { IncoOplValidationMeasures, IncoOplValidationMeasuresResponse } from 'src/app/interfaces/investmentcontrolling/inco-opl-reporting-response';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-opl-validation',
  templateUrl: './opl-validation.component.html',
  styleUrls: ['./opl-validation.component.less'],
  standalone: false
})
export class OplValidationComponent implements OnInit {
  isLoading: boolean = false;
  currencyShort: string = 'Local Currency';
  currencySymbol: string = '-';
  periods!: IncoOplPeriodsInfo;
  verificationStatus: 'success' | 'error' | '' = '';
  isVerified: boolean = false;
  showOnlyErrors: boolean = false;
  showColumnFilters: boolean = false;
  tableColumns: TableColumn[] = [];
  validationOplMeasures: IncoOplValidationMeasures[] = [];
  displayedMeasures: IncoOplValidationMeasures[] = [];

  private readonly destroy$ = new Subject<void>();
  rowValidationStatus: Map<string, 'valid' | 'invalid'> = new Map();
  cellValidationStatus: Map<string, 'valid' | 'invalid'> = new Map();

  toggleShowOnlyErrors(): void {
    this.showOnlyErrors = !this.showOnlyErrors;
    this.updateDisplayedMeasures();
  }

  private updateDisplayedMeasures(): void {
    this.displayedMeasures = this.showOnlyErrors
      ? this.validationOplMeasures.filter(row => this.rowValidationStatus.get(row.managementId) === 'invalid')
      : this.validationOplMeasures;
  }

  constructor(
    public defaults: DefaultValuesService,
    private incoService: InvestmentControllingService,
    private readonly toastrService: ToastrService,
    private readonly dialog: DialogService,
  ) { }

  ngOnInit(): void {
    this.setupSubscriptions();
  }

  private setupSubscriptions(): void {
    merge(
      this.defaults.fiscalYearMessageChanges$
    ).pipe(
      startWith(null),
      debounceTime(50),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.loadData();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeColumns(): void {
    const fcYearLabel = this.periods?.fc?.label || '';
    const budYearLabel = this.periods?.bud?.label || '';
    const pl1YearLabel = this.periods?.pl1?.label || '';
    const pl2YearLabel = this.periods?.pl2?.label || '';

    const baseColumns: TableColumn[] = [
      {
        key: 'managementBu',
        header: 'BU',
        width: '6%',
        align: 'left',
        editable: false,
        filterable: true,
      },
      {
        key: 'managementId',
        header: 'Management ID',
        width: '8%',
        align: 'left',
        editable: false,
        filterable: true,
        cellClass: (_value, row, _rowIndex) => {
          if (!row?.managementId) return null;
          const status = this.rowValidationStatus.get(row.managementId);
          return TableUtils.getValidationClass(status);
        }
      },
      {
        key: 'managementName',
        header: 'Management Name',
        width: '20%',
        align: 'left',
        editable: false,
        filterable: true,
      },
      {
        key: 'localCurrency',
        header: 'Local Currency',
        width: '6%',
        align: 'left',
        editable: false,
      },
    ];

    const forecastColumns: TableColumn[] = [
      {
        key: 'fcTotal',
        header: 'FC',
        headerSub: fcYearLabel,
        width: '8%',
        align: 'right',
        type: 'currency',
        editable: false,
        formatter: (value, _row, index) => {
          return TableUtils.formatCurrency(value);
        },
        cellClass: (_value, row, _rowIndex) => {
          if (!row?.managementId) return null;
          const status = this.cellValidationStatus.get(`${row.managementId}_fcTotal`);
          return TableUtils.getValidationClass(status);
        }
      },
      {
        key: 'sapFcTotal',
        header: 'FC SAP',
        headerSub: fcYearLabel,
        width: '8%',
        align: 'right',
        type: 'currency',
        editable: false,
        formatter: (value, _row, index) => {
          return TableUtils.formatCurrency(value);
        },
      },
      {
        key: 'budTotal',
        header: 'BUD',
        headerSub: budYearLabel,
        width: '8%',
        align: 'right',
        type: 'currency',
        editable: true,
        formatter: (value, _row, index) => {
          return TableUtils.formatCurrency(value);
        },
        cellClass: (_value, row, _rowIndex) => {
          if (!row?.managementId) return null;
          const status = this.cellValidationStatus.get(`${row.managementId}_budTotal`);
          return TableUtils.getValidationClass(status);
        }
      },
      {
        key: 'sapBudTotal',
        header: 'BUD SAP',
        headerSub: budYearLabel,
        width: '8%',
        align: 'right',
        type: 'currency',
        editable: false,
        formatter: (value, _row, index) => {
          return TableUtils.formatCurrency(value);
        }
      },
      {
        key: 'pl1Total',
        header: 'PL1',
        headerSub: pl1YearLabel,
        width: '8%',
        align: 'right',
        type: 'currency',
        editable: true,
        formatter: (value, _row, index) => {
          return TableUtils.formatCurrency(value);
        },
        cellClass: (_value, row, _rowIndex) => {
          if (!row?.managementId) return null;
          const status = this.cellValidationStatus.get(`${row.managementId}_pl1Total`);
          return TableUtils.getValidationClass(status);
        }
      },
      {
        key: 'sapPl1Total',
        header: 'PL1 SAP',
        headerSub: pl1YearLabel,
        width: '8%',
        align: 'right',
        type: 'currency',
        editable: false,
        formatter: (value, _row, index) => {
          return TableUtils.formatCurrency(value);
        },
      },
      {
        key: 'pl2Total',
        header: 'PL2',
        headerSub: pl2YearLabel,
        width: '8%',
        align: 'right',
        type: 'currency',
        editable: true,
        formatter: (value, _row, index) => {
          return TableUtils.formatCurrency(value);
        },
        cellClass: (_value, row, _rowIndex) => {
          if (!row?.managementId) return null;
          const status = this.cellValidationStatus.get(`${row.managementId}_pl2Total`);
          return TableUtils.getValidationClass(status);
        }
      },
      {
        key: 'sapPl2Total',
        header: 'PL2 SAP',
        headerSub: pl2YearLabel,
        width: '8%',
        align: 'right',
        type: 'currency',
        editable: false,
        formatter: (value, _row, index) => {
          return TableUtils.formatCurrency(value);
        },
      }
    ];

    this.tableColumns = [...baseColumns, ...forecastColumns];

    // const historyColumn: TableColumn = {
    //   key: 'history',
    //   header: 'Change Log',
    //   width: '4%',
    //   align: 'center',
    //   editable: false,
    //   formatter: (_value, row, index) => {
    //     if (index === 0) return '-';
    //     return row.id || row.projectId ? 'show' : '-';
    //   },
    //   cellClass: (_value, _row, index) => index === 0 ? 'total-row' : 'history-cell',
    // };

    // this.tableColumns.push(historyColumn);
  }

  loadData(showLoading: boolean = true, force: boolean = false): void {
    if (showLoading) {
      this.isLoading = true;
    }
    this.verificationStatus = '';

    this.incoService.loadOplValidation(
      this.defaults.fiscalYear,
      this,
      (tthis: OplValidationComponent, result: IncoOplValidationMeasuresResponse) => {
        tthis.periods = result.periods;
        tthis.validationOplMeasures = result.measures;
        tthis.initializeColumns();
        tthis.isLoading = false;
        tthis.showOnlyErrors = false;
        tthis.rowValidationStatus.clear();
        tthis.cellValidationStatus.clear();
        tthis.updateDisplayedMeasures();
      },
      (tthis: OplValidationComponent, error: any) => {
        tthis.dialog.showErrorDialog(error);
        tthis.isLoading = false;
      }
    );
  }

  onClickVerify(): void {
    this.rowValidationStatus.clear();
    this.cellValidationStatus.clear();

    let allRowsValid = true;

    for (let rowIndex = 0; rowIndex < this.validationOplMeasures.length; rowIndex++) {
      const row = this.validationOplMeasures[rowIndex];
      const id = row.managementId;

      const fcValid = row.fcTotal.toFixed(2) === row.sapFcTotal.toFixed(2);
      const budValid = row.budTotal.toFixed(2) === row.sapBudTotal.toFixed(2);
      const pl1Valid = row.pl1Total.toFixed(2) === row.sapPl1Total.toFixed(2);
      const pl2Valid = row.pl2Total.toFixed(2) === row.sapPl2Total.toFixed(2);

      this.cellValidationStatus.set(`${id}_fcTotal`, fcValid ? 'valid' : 'invalid');
      this.cellValidationStatus.set(`${id}_budTotal`, budValid ? 'valid' : 'invalid');
      this.cellValidationStatus.set(`${id}_pl1Total`, pl1Valid ? 'valid' : 'invalid');
      this.cellValidationStatus.set(`${id}_pl2Total`, pl2Valid ? 'valid' : 'invalid');

      const rowIsValid = fcValid && budValid && pl1Valid && pl2Valid;
      this.rowValidationStatus.set(id, rowIsValid ? 'valid' : 'invalid');
      if (!rowIsValid) allRowsValid = false;
    }

    if (allRowsValid) {
      this.verificationStatus = 'success';
      this.isVerified = true;
      this.toastrService.success('All rows match SAP values.');
    } else {
      this.verificationStatus = 'error';
      this.isVerified = false;
      this.toastrService.warning('Some rows do not match with SAP. Please check the highlighted rows in red.');
    }

    this.updateDisplayedMeasures();
    this.initializeColumns();
  }
}
